<template>
    <div class="container pt-5">
        <div class="form-group">
            <div class="my-2 input-group">
                <div class="input-group-prepend">
                    <span class="input-group-text">
                        <i class="fas fa-search"></i>
                    </span>
                </div>
                <input type="search" class="form-control form-control" v-model="keywords" :placeholder="placeholder" @keydown.enter="search" />
            </div>
        </div>
        <div v-if="results.total" class="results">
            <div class="row justify-content-center">
                <div class="col-md-12">
                    <div class="sticky">
                        <page :last_page="results.last_page" v-model="page" @goto="goto"></page>
                    </div>
                    <div class="card mb-3 position-relative"
                        v-for="v in results.data" :key="v.id"
                        :class="{
                            'bg-deleted': v.deleted_at,
                            'bg-light': !v.deleted_at
                        }">
                        <div class="filtered" v-if="isFilter(v)" @click="showR18()">
                            <span class="badge badge-danger">R18作品</span>
                        </div>
                        <component
                            :is="isInternal(v) ? 'router-link' : 'a'"
                            :to="isInternal(v) ? bookUrl(v): ''"
                            :href="!isInternal(v) ? bookUrl(v): ''"
                            :target="isInternal(v) ? '_self' : v.subdomain"
                            class="black">
                            <div class="row no-gutters">
                                <div class="col-xl img-column" v-if="v.cover_url">
                                    <img v-lazy="v.cover_url.replace(/\.webp$/, '_thumb.webp') + '?' + v.cover_hash" class="mx-auto d-block cover" />
                                </div>
                                <div class="col-xl img-column" v-else>
                                    <img v-lazy="cover()" class="mx-auto d-block cover" />
                                    <div class="book-text">
                                        <div class="cover-text vertical">
                                            <h3 class="novel-title mincho" v-html="$filters.ruby(v.title)"></h3>
                                            <p v-if="v.subtitle" v-html="$filters.ruby(v.subtitle)"></p>
                                        </div>
                                    </div>
                                </div>
                                <div class="col-xl">
                                    <div class="card-body">
                                        <div class="row">
                                            <div class="col-lg-9">
                                                <div class="card-title">
                                                    <h3 class="title mincho">{{v.title}}</h3>
                                                    <span class="badge" :class="['badge-' + v.color]" v-if="v.rating_name">{{v.rating_name}}</span>
                                                    <span class="badge badge-dark" v-if="v.deleted_at">{{$filters.fromNow(v.deleted_at )}} に公開停止</span>
                                                    <i class="fas fa-lock" v-if="isRoot && v.override_at"></i>
                                                    <div class="text-muted small">
                                                        <ruby>{{v.name}}<rt>{{v.kana}}</rt></ruby>
                                                    </div>
                                                </div>
                                                <h6 class="card-subtitle mincho" v-if="v.subtitle">{{v.subtitle}}</h6>
                                            </div>
                                            <div class="col-lg-3 mt-2">
                                                <button type="button"
                                                    class="btn btn-sm btn-block"
                                                    :class="{'btn-outline-danger': !v.deleted_at, 'btn-outline-success': v.deleted_at}"
                                                    v-if="isRoot"
                                                    @click.prevent="toggleBook(v)">
                                                    <span v-if="v.deleted_at">
                                                        <i class="fas fa-thumbs-up"></i>
                                                        再公開
                                                    </span>
                                                    <span v-else>
                                                        <i class="fas fa-ban"></i>
                                                        公開停止
                                                    </span>
                                                </button>
                                                <button type="button"
                                                    class="btn btn-sm btn-block btn-outline-secondary"
                                                    v-if="isRoot"
                                                    @click.prevent="showStatus(v)">
                                                    レーティング
                                                </button>
                                            </div>
                                        </div>
                                        <div class="card-text desc b-air">
                                            {{$filters.truncate(v.description , 200)}}
                                        </div>
                                        <div v-if="v.tags">
                                            <ul class="tag-list">
                                                <li v-for="t in v.tags" :key="t.id">{{t.tag_name}}</li>
                                            </ul>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </component>
                    </div>
                </div>
            </div>
        </div>
        <div v-else class="nothing">
            検索結果はありません。
        </div>
        <status :id="book_id" @saved="load()"></status>
        <loading
            v-model:active="isLoading"
            :can-cancel="false"
            :is-full-page="true"></loading>
    </div>
</template>

<style scoped lang="scss">
.cover {
    object-fit: cover;
    height: 352px;
    width: 250px;
}
.desc {
    margin: 1em 0;
    padding: .7em;
    border-radius: .5rem;
    font-size: .8rem;
}
.img-column {
    max-width: 250px;
    .book-text {
        position: absolute;
        top: 0;
        left: 0;
        padding: 2.3rem;
        height: 352px;
        min-width: 250px;
        width: 100%;
        overflow: hidden;
        text-align: center;
        color: snow;
        .cover-text {
            display: inline-block;
            text-align: left;
            h3 {
                font-size: 1.0rem;
            }
            p {
                font-size: .7rem;
            }
        }
    }
}
.title {
    display: inline-block;
}
.bg-deleted {
    background-color: #dfd3d3;
}
.btn-tag {
    margin: .5rem;
    border-width: 1px;
    border-radius: 1rem;
}
@media screen and (max-width: 1200px) {
    .img-column {
        max-width: none;
    }
}
</style>

<script>
import status from "./admin/books/status";
import page from "components/Page";
export default {
    components: {
        status,
        page,
    },
    data() {
        return {
            keywords: this.$route.query.keywords || "",
            page: parseInt(this.$route.query.page) || 1,
            results: {},
            book_id: null,
            isLoading: false,
            routeWatcher: null,
        }
    },
    methods: {
        goto(p) {
            this.page = p;
            setTimeout(() => {this.load()});
        },
        search(ev) {
            if (ev && ev.keyCode !== 13) return;
            this.$router.push("/search?keywords=" + encodeURI(this.keywords));
        },
        load() {
            this.isLoading = true;
            $("html,body").animate({scrollTop: 0}, 300);
            axios.get("/find", {params: {
                keywords: this.keywords,
                page: this.page,
            }}).then(res => {
                this.results = res.data;
                this.isLoading = false;
            }).catch(err => {
                alert.err(err.response);
                this.isLoading = false;
            });
        },
        bookUrl(b) {
            let subdomain = "www";
            if (b.subdomain) {
                subdomain = b.subdomain;
            }
            let url = "";
            if (subdomain + this.$store.state.base_domain != location.hostname) {
                url = "//" + subdomain + this.$store.state.base_domain;
            }
            if (b.is_long) {
                // 長編
                url += "/author/book/" + b.id;
            } else {
                url += "/author/page/" + b.id + "/section/" + b.section.id;
            }
            return url;
        },
        isInternal(v) {
            return !this.bookUrl(v).match(/^(https?:)?\/\//);
        },
        find(keywords) {
            this.keywords = keywords;
            this.search();
        },
        toggleBook(b) {
            // 管理者用
            let title = "書籍の公開停止";
            let text = `「${b.title}」を公開停止にします。\n再公開まで表示されません。\nよろしいですか？`;
            let btn = "公開停止";
            let icon = "error";
            if (b.deleted_at) {
                title = "書籍の再公開";
                text = `「${b.title}」を再公開します。よろしいですか？`;
                btn = "再公開";
                icon = "success";
            }
            Swal.fire({
                title: title,
                text: text,
                icon: icon,
                showCancelButton: true,
                confirmButtonText: btn,
                cancelButtonText: "キャンセル",
                confirmButtonColor: "crimson",
                cancelButtonColor: "gray",
                reverseButtons: true,
            }).then(confirm => {
                if (confirm.isConfirmed) {
                    axios.post("/admin/book/toggle", {'id': b.id}).then(res => {
                        alert.msg(res.data);
                        this.load();
                    }).catch(err => { alert.err(err.response); });
                }
            });
        },
        showStatus(b) {
            // 管理者用
            this.book_id = b.id;
            setTimeout(() => {
                $("#status-modal").modal();
            });
        },
        cover() {
            const index = Math.floor(Math.random() * this.$store.getters.novelCovers.length);
            return this.$store.getters.novelCovers[index];
        },
        isFilter(v) {
            if (v.rating_name === "R18" && !this.$store.state.user && !this.$store.state.r18) {
                return true;
            }
            return false;
        },
        showR18() {
            $("#r18-modal").modal();
        },
    },
    computed: {
        isRoot() {
            return this.$store.state?.user?.is_root;
        },
        placeholder() {
            if (this.$store.state.is_isolated) {
                if (this.$store.state.bookshelf_name) {
                    return this.$store.state.bookshelf_name + " の書籍検索";
                }
            }
            return "書籍検索";
        },
    },
    mounted() {
        this.load();
        this.routeWatcher = this.$watch('$route', (n, o) => {
            if (n.query.keywords !== this.keywords) {
                this.keywords = n.query.keywords;
            }
            this.load();
        });
        this.useHead({
            title: "検索 | 貸し本棚",
            link: [ { rel: "canonical", href: location.origin + location.pathname, id: "canonical" } ]
        });
    }
}
</script>